/**
 * Speech-to-Text Service
 *
 * Transcribes one buffered patient turn of audio via the chartmindSTT Cloud Function (OpenAI
 * transcription, server-side, under our OpenAI BAA + Zero Data Retention). This is the INPUT half of
 * CareStart voice; ttsService is the OUTPUT half. Together they keep patient audio (PHI) on a
 * BAA-covered subprocessor instead of the Gemini Live Developer API.
 *
 * Turn-based, not streaming: the voice engine records the patient turn, then calls transcribe() on
 * turn end. The returned text is the patient's OWN-LANGUAGE transcript; the engine translates it to
 * English (Vertex) before running the shared brain — same contract Gemini's inputTranscription had.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

/** Read a Blob's bytes as a bare base64 string (no data: URL prefix). */
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Could not read audio blob'));
    reader.onloadend = () => {
      const result = String(reader.result || '');
      // FileReader.readAsDataURL yields "data:<mime>;base64,<data>"; strip the prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });

/**
 * Transcribe one turn of patient audio.
 *
 * @param {Blob} audioBlob - Recorded audio for a single patient turn.
 * @param {Object} [options]
 * @param {string} [options.language] - BCP-47 hint for the spoken language (improves accuracy).
 * @param {string} [options.model] - Transcription model override (allowlisted server-side).
 * @param {string} [options.prompt] - Bias text passed to the transcriber. ChartMind names the
 *   clinician's selected language here, because the language hint alone is soft and a near-silent
 *   segment can otherwise come back in an unrelated language.
 * @returns {Promise<string>} The transcript, or '' when nothing usable was captured.
 */
export const transcribe = async (audioBlob, options = {}) => {
  if (!audioBlob || !audioBlob.size) return '';

  const audioBase64 = await blobToBase64(audioBlob);
  if (!audioBase64) return '';

  const functions = getFunctions();
  const chartmindSTT = httpsCallable(functions, 'chartmindSTT');
  const result = await chartmindSTT({
    audioBase64,
    mimeType: audioBlob.type || 'audio/webm',
    ...(options.language ? { language: options.language } : {}),
    ...(options.model ? { model: options.model } : {}),
    ...(options.prompt ? { prompt: options.prompt } : {}),
  });
  return String(result?.data?.text || '').trim();
};

/**
 * Warm the chartmindSTT Cloud Function (spin up an instance + init the OpenAI client) so the first
 * real transcription of a session does not pay the cold-start penalty. Fire-and-forget; never throws.
 * @returns {Promise<void>}
 */
export const warmUp = async () => {
  try {
    const functions = getFunctions();
    const chartmindSTT = httpsCallable(functions, 'chartmindSTT');
    await chartmindSTT({ warm: true });
  } catch (e) {
    console.warn('[sttService] warmUp failed (non-fatal):', e?.message);
  }
};

const sttService = {
  transcribe,
  warmUp,
};

export default sttService;
